<template>
  <button
    class="flex w-full cursor-pointer items-center justify-center rounded-full border-none bg-white py-3 text-black hover:bg-nordy"
    @click="handleDiscordLogin"
  >
    <span class="pi pi-discord mr-2 text-2xl"></span>
    {{ loginDiscord }}
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { nordyApi } from '@/nordy/api'
import { useNordyUserStore } from '@/nordy/store/nordyUserStore'
import { trackNordyAuth } from '@/nordy/utils/gtm'
import { Gtag } from '@/nordy/utils/gtag'
import { getMixpanelAdditionalInfo } from '@/nordy/utils/mixpanelAdditionalInfo'
import { saveOAuthAttributionSnapshot } from '@/nordy/utils/mixpanelOAuthAttribution'

const nordyUserStore = useNordyUserStore()
const isKo = computed(() => nordyUserStore.nordyUser?.language === 'ko')
const loginDiscord = computed(() =>
  isKo.value ? 'Discord로 로그인' : 'Sign in with Discord'
)
const popupBlockedMsg = computed(() =>
  isKo.value
    ? '팝업이 차단되었습니다. 이 사이트의 팝업을 허용해 주세요.'
    : 'Pop-up was blocked. Please allow pop-ups for this site.'
)

const emit = defineEmits<{
  (e: 'loginSuccess'): void
}>()

const handleDiscordLogin = async () => {
  const entryPath = window.location.pathname
  Gtag.comfyui_login_click({ provider: 'discord' })
  saveOAuthAttributionSnapshot(getMixpanelAdditionalInfo(), {
    $current_url: window.location.href
  })

  try {
    const discordClientId = import.meta.env.VITE_DISCORD_CLIENT_ID
    const redirectUri =
      window.location.origin + window.location.pathname + 'discord'

    const scope = encodeURIComponent('identify email')
    const oauthUrl = `https://discord.com/api/oauth2/authorize?client_id=${discordClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scope}`

    const popup = window.open(oauthUrl, 'discordLogin', 'width=500,height=1000')
    if (!popup) {
      alert(popupBlockedMsg)
      return
    }
    const { code, error }: { code?: string; error?: string } =
      await getPopupCode(popup, 'discord')

    if (code) {
      const response = await nordyApi.signupOrLoginByDiscord({
        code,
        redirect: redirectUri
      })
      if (response) {
        trackNordyAuth(response, 'discord', entryPath)
        emit('loginSuccess')
        Gtag.comfyui_login_success({ provider: 'discord' })
      }
    }

    if (error) {
      throw new Error(error)
    }
  } catch (error) {
    console.error(error)
  }
}

function getPopupCode(
  popup: Window,
  provider: string
): Promise<{
  code?: string
  error?: string
}> {
  return new Promise((resolve, reject) => {
    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      //!nordy - popup source 검증으로 같은 origin의 다른 스크립트 스푸핑 방지
      if (event.source !== popup) return
      if (event.data && event.data.provider === provider && event.data.code) {
        resolve({ code: event.data.code })
        window.removeEventListener('message', listener)
        popup.close()
      } else if (
        event.data &&
        event.data.provider === provider &&
        event.data.error
      ) {
        resolve({ error: event.data.error })
        window.removeEventListener('message', listener)
        popup.close()
      }
    }
    window.addEventListener('message', listener)
    setTimeout(() => {
      window.removeEventListener('message', listener)
      popup.close()
      reject(new Error('Timeout waiting for auth code'))
    }, 300000)
  })
}
</script>
